import { Link } from 'react-router-dom';
import WithdrawFeeScheduleBox from '../../components/withdraw-fee-schedule/withdraw-fee-schedule';
import React, { useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import toast from 'react-hot-toast';
import { Store, Tag, Sparkles, Boxes, ListTree, User, Lock, ArrowDown } from 'lucide-react';
import DialogPayment from '../../components/dialog-payment';
import AvatarFrame from '../../components/cosmetics/avatar-frame';
import Nametag from '../../components/cosmetics/nametag.component';
import { FrameRarity } from '../../components/cosmetics/cosmetic-visuals';
import DialogComponent from '../../components/dialog/dialog.component';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { createAmount } from '../../api';
import {
  getStoreItems,
  getInventory,
  getWalletSummary,
  purchaseStoreItem,
  equipStoreItem,
  StoreItem,
  InventoryItem,
  WalletSummary,
  getUserBalanceBuckets,
  UserBalanceBuckets,
} from '../../api/payment/store.api';
import { getAffRef } from '../../utils/common/aff-ref';
import { useTopupPricing } from '../../hooks/useTopupPricing';

const fmtCa = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')} Ca`;
const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

// Mệnh giá nạp Ca — chỉ là chọn số tiền nạp, KHÔNG phải "gói" có ưu đãi khác nhau.
// Bỏ mệnh giá 10.000đ: phí cố định của cổng (2.200đ/giao dịch) không chia theo số tiền nên
// gói này bị đội ~25%, nhìn rất bất lợi. Ai vẫn muốn nạp nhỏ thì dùng ô "Nhập số tiền khác"
// trong hộp nạp — ở đó có bảng tách phí đầy đủ trước khi xác nhận.
const TOPUP_PACKAGES = [50000, 100000, 500000];

// token design (Figma get_design_context node 10:1292)
const PRICE = 'bg-gradient-to-r from-[#00a8e2] to-[#00669a] bg-clip-text text-transparent';
const PRICE_BOX =
  'flex w-full items-center justify-center rounded-[12px] border border-[#48afff] px-2.5 py-2 dark:border-[#2f6d9c] dark:bg-white/5';
const BTN = 'w-full rounded-full bg-[#066bff] py-2 text-[16px] font-medium text-white transition hover:brightness-105 disabled:opacity-60';
const CARD =
  'flex w-full flex-col items-center gap-4 rounded-[20px] bg-white p-6 shadow-[4px_4px_44px_rgba(0,0,0,0.08)] dark:bg-slate-800 dark:shadow-none';

const RARITIES: FrameRarity[] = ['common', 'rare', 'epic', 'legendary', 'mythic'];
const rarityOf = (item: StoreItem): FrameRarity | null => {
  const r = (item.rarity || '').toLowerCase();
  return (RARITIES as string[]).includes(r) ? (r as FrameRarity) : null;
};

// Tình trạng suất phát hành (khớp mốc chặn mua ở BE: tháng theo giờ VN, năm theo UTC).
const availabilityOf = (item: StoreItem) => {
  const remMonth =
    item.monthlyLimit != null ? Math.max(0, item.monthlyLimit - (item.soldThisMonth ?? 0)) : null;
  const remYear =
    item.yearlyLimit != null ? Math.max(0, item.yearlyLimit - (item.soldThisYear ?? 0)) : null;
  const limited = item.monthlyLimit != null || item.yearlyLimit != null;
  const soldOutMonth = remMonth === 0;
  const soldOutYear = remYear === 0;
  return { remMonth, remYear, limited, soldOutMonth, soldOutYear, soldOut: soldOutMonth || soldOutYear };
};

// Preview hiệu ứng: render THẬT khung tên / khung avatar theo bậc (không cần user sở hữu).
const PreviewAvatar: React.FC<{ rarity: FrameRarity }> = ({ rarity }) => {
  const PX = 64;
  return (
    <div style={{ position: 'relative', width: PX, height: PX }}>
      <div
        style={{ width: PX, height: PX, borderRadius: '50%' }}
        className="flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700"
      >
        <User size={30} className="text-slate-500 dark:text-slate-300" />
      </div>
      <AvatarFrame rarity={rarity} sizePx={PX} withParticles />
    </div>
  );
};

const CosmeticPreview: React.FC<{ item: StoreItem; rarity: FrameRarity }> = ({ item, rarity }) => (
  <div className="flex h-[132px] w-full items-center justify-center overflow-visible">
    {item.type === 'Nametag' ? (
      <Nametag previewRarity={rarity} banner compact>
        <span className="font-semibold">Tên của bạn</span>
      </Nametag>
    ) : (
      <PreviewAvatar rarity={rarity} />
    )}
  </div>
);

// Đếm ngược tới 0h ngày 1 tháng sau (giờ VN, UTC+7) — dùng khi Thần Thoại hết suất tháng.
const msToNextVnMonth = () => {
  const now = Date.now();
  const vn = new Date(now + 7 * 3600 * 1000); // giờ VN (đọc bằng getUTC*)
  const targetVnWall = Date.UTC(vn.getUTCFullYear(), vn.getUTCMonth() + 1, 1, 0, 0, 0);
  return targetVnWall - 7 * 3600 * 1000 - now; // mốc UTC thật - hiện tại
};

const MonthResetCountdown: React.FC = () => {
  const [ms, setMs] = useState(msToNextVnMonth());
  useEffect(() => {
    const t = setInterval(() => setMs(msToNextVnMonth()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    <span className="tabular-nums font-semibold">
      {d > 0 ? `${d} ngày ` : ''}
      {pad(Math.floor((s % 86400) / 3600))}:{pad(Math.floor((s % 3600) / 60))}:{pad(s % 60)}
    </span>
  );
};

// Popup "Xem thử": render hiệu ứng của món lên ĐÚNG avatar + tên của user hiện tại.
const TryOnPreview: React.FC<{
  item: StoreItem;
  rarity: FrameRarity | null;
  avatar: string;
  name: string;
}> = ({ item, rarity, avatar, name }) => {
  const AV = 120;
  const showFrame = !!rarity && item.type === 'AvatarEffect';
  const showBanner = !!rarity && item.type === 'Nametag';
  return (
    <div className="flex flex-col items-center gap-7 px-6 py-8">
      <p className="text-center text-[14px] text-slate-500 dark:text-slate-300">
        Đây là cách{' '}
        <b className="text-slate-700 dark:text-white">{item.name}</b> hiển thị trên hồ sơ của bạn:
      </p>
      <div style={{ position: 'relative', width: AV, height: AV }}>
        {avatar ? (
          <img
            src={avatar}
            alt=""
            style={{ width: AV, height: AV }}
            className="rounded-full object-cover"
          />
        ) : (
          <div
            style={{ width: AV, height: AV, borderRadius: '50%' }}
            className="flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700"
          >
            <User size={54} className="text-slate-500 dark:text-slate-300" />
          </div>
        )}
        {showFrame && <AvatarFrame rarity={rarity as FrameRarity} sizePx={AV} withParticles />}
      </div>
      {showBanner ? (
        <Nametag previewRarity={rarity as FrameRarity} banner>
          <span className="font-semibold">{name}</span>
        </Nametag>
      ) : (
        <span className="text-[18px] font-semibold text-slate-800 dark:text-white">{name}</span>
      )}
    </div>
  );
};

// Dòng thông tin suất khả dụng / đã bán / số người sở hữu (FOMO).
const AvailabilityInfo: React.FC<{ item: StoreItem }> = ({ item }) => {
  const owners = item.ownerCount ?? 0;
  const ownersVi = owners.toLocaleString('vi-VN');
  const { limited, remMonth, soldOutMonth, soldOutYear } = availabilityOf(item);

  if (!limited) {
    return (
      <p className="text-center text-[13px] font-medium text-slate-500 dark:text-slate-400">
        {owners > 0 ? `🔥 ${ownersVi} người đang dùng` : '✨ Hãy là người đầu tiên sở hữu!'}
      </p>
    );
  }

  const ownerLine = owners > 0 && (
    <span className="text-[12px] text-slate-500 dark:text-slate-400">{ownersVi} người đang sở hữu</span>
  );

  if (soldOutYear) {
    return (
      <div className="flex flex-col items-center gap-1 text-center">
        <span className="rounded-full bg-slate-100 px-3 py-1 text-[13px] font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-300">
          Đã bán hết {item.yearlyLimit} suất năm nay — hẹn năm sau
        </span>
        {ownerLine}
      </div>
    );
  }

  if (soldOutMonth) {
    return (
      <div className="flex flex-col items-center gap-1 text-center">
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1 text-[13px] font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-300">
          <Lock size={13} /> Hết suất tháng này
        </span>
        <span className="text-[12px] text-slate-500 dark:text-slate-400">
          Mở lại sau: <MonthResetCountdown />
        </span>
        {ownerLine}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 text-center">
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-[13px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
        🔥 Tháng này còn {remMonth} suất
      </span>
      <span className="text-[12px] text-slate-500 dark:text-slate-400">
        Đã bán {item.soldThisYear ?? 0}/{item.yearlyLimit} năm nay
        {owners > 0 ? ` · ${ownersVi} đang sở hữu` : ''}
      </span>
    </div>
  );
};

// QA S-30: khai ở module (KHÔNG trong render WalletPage) để tránh remount subtree mỗi lần render.
const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="flex w-full scroll-mt-[88px] flex-col gap-6">
    <h2 className="px-2 text-[24px] font-semibold tracking-tight text-[#19052f] dark:text-white">{title}</h2>
    <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  </section>
);

const WalletPage: React.FC<{ walletOnly?: boolean }> = ({ walletOnly = false }) => {
  const [summary, setSummary] = useState<WalletSummary | null>(null);
  const [buckets, setBuckets] = useState<UserBalanceBuckets | null>(null);
  const [items, setItems] = useState<StoreItem[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  const [tryOn, setTryOn] = useState<StoreItem | null>(null); // món đang "Xem thử"

  const profile = useSelector((s: RootState) => s.features.auth.profile);
  const myAvatar = ((profile as any)?.avatar as string) || '';
  const myName = ((profile as any)?.fullName || (profile as any)?.username || 'Bạn') as string;

  const loadSummary = () =>
    Promise.all([
      getWalletSummary().then(setSummary).catch(() => {}),
      getUserBalanceBuckets().then(setBuckets).catch(() => {}),
    ]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [its, inv, sum, bkt] = await Promise.all([
        getStoreItems(),
        getInventory(),
        getWalletSummary(),
        getUserBalanceBuckets().catch(() => null),
      ]);
      setItems(its || []);
      setInventory(inv || []);
      setSummary(sum);
      setBuckets(bkt);
    } catch {
      /* rỗng */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ownedOf = (itemId: string) => inventory.find((i) => i.storeItemId === itemId);

  const handleBuy = async (item: StoreItem) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      const res = await purchaseStoreItem(item.id, getAffRef());
      if (res.success) {
        toast.success(`Đã mua "${item.name}"`);
        const [inv] = await Promise.all([getInventory(), loadSummary()]);
        setInventory(inv || []);
      } else {
        toast.error(res.message || 'Mua không thành công');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Có lỗi khi mua vật phẩm');
    } finally {
      setBusyId(null);
    }
  };

  const handleEquip = async (inv: InventoryItem) => {
    if (busyId) return;
    setBusyId(inv.id);
    try {
      const res = await equipStoreItem(inv.id, !inv.isEquipped);
      if (res.success) {
        const list = await getInventory();
        setInventory(list || []);
      } else {
        toast.error(res.message || 'Không thực hiện được');
      }
    } finally {
      setBusyId(null);
    }
  };

  // Phí cổng + tỉ giá lấy từ cấu hình đang chạy (khớp BE), để thẻ nạp hiện đúng số thực trả.
  const pricing = useTopupPricing();

  const handleTopup = (baseVnd: number) => {
    createAmount(baseVnd)
      .then((res: any) => {
        if (res?.paymentUrl) window.location.href = res.paymentUrl;
      })
      .catch(() => toast.error('Không tạo được yêu cầu nạp'));
  };

  const nametags = items.filter((i) => i.type === 'Nametag');
  const effects = items.filter((i) => i.type === 'AvatarEffect');
  const others = items.filter((i) =>
    ['SuperVote', 'DatingTurn', 'ClanCreate', 'ClanExpand'].includes(i.type)
  );

  // Mục lục (table of content) — chỉ hiện mục có dữ liệu
  const SECTIONS = useMemo(
    () =>
      [
        { id: 'store-marketing', title: 'Nạp Ca', Icon: Store, show: true },
        { id: 'store-nametag', title: 'Nametag đặc biệt', Icon: Tag, show: nametags.length > 0 },
        { id: 'store-effect', title: 'Hiệu ứng avatar', Icon: Sparkles, show: effects.length > 0 },
        { id: 'store-other', title: 'Khác', Icon: Boxes, show: others.length > 0 },
      ].filter((s) => s.show),
    [nametags.length, effects.length, others.length]
  );

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  // Scroll-spy: tô sáng mục đang xem trong mục lục
  useEffect(() => {
    if (loading || SECTIONS.length === 0) return;
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => !!el
    );
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-88px 0px -55% 0px', threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    setActiveSection((prev) => prev || SECTIONS[0].id);
    return () => obs.disconnect();
  }, [loading, SECTIONS]);

  // QA S-30: render-function (KHÔNG khai component trong render) để không remount cả subtree mỗi lần WalletPage render.
  const renderItemCard = (item: StoreItem) => {
    const owned = ownedOf(item.id);
    // Cosmetic (gắn/tháo) = Nametag/AvatarEffect KHÔNG cộng dồn — gồm cả Thần Thoại (đã bỏ
    // IsConsumable, mua rồi thì gắn/tháo như Huyền Thoại). Luồng số lượng "Mua thêm (đang có X)"
    // chỉ còn cho đồ tiêu hao thật (super vote, vé clan…).
    const isCosmetic =
      (item.type === 'Nametag' || item.type === 'AvatarEffect') && !item.isConsumable;
    const rarity = rarityOf(item);
    const { soldOut } = availabilityOf(item);
    return (
      <div key={item.id} className={CARD}>
        <div className="text-center text-[20px] font-semibold tracking-tight text-[#19052f] dark:text-white">
          {item.name}
        </div>
        {/* Preview hiệu ứng — cho user chưa mua thấy "mua xong được cái này" */}
        {isCosmetic && rarity && <CosmeticPreview item={item} rarity={rarity} />}
        {isCosmetic && rarity && (
          <button
            onClick={() => setTryOn(item)}
            className="-mt-1 text-[13px] font-medium text-[#066bff] hover:underline dark:text-[#60a5fa]"
          >
            🔍 Xem thử
          </button>
        )}
        <div className={PRICE_BOX}>
          <span className={`text-[30px] font-semibold leading-[38px] ${PRICE}`}>
            {fmtCa(item.priceCa)}
          </span>
        </div>
        {item.description && (
          <p className="min-h-[40px] flex-1 text-center text-[13px] font-normal leading-5 text-slate-500 dark:text-slate-400">
            {item.description}
          </p>
        )}
        {/* Suất khả dụng / đã bán / số người sở hữu */}
        {isCosmetic && <AvailabilityInfo item={item} />}
        {isCosmetic && owned ? (
          <button
            onClick={() => handleEquip(owned)}
            disabled={busyId === owned.id}
            className={`w-full rounded-full py-2 text-[16px] font-medium transition ${
              owned.isEquipped
                ? 'border border-[#1E7D34] bg-[#ECFDF3] text-[#1E7D34] dark:border-[#2e7d4f] dark:bg-[#123524] dark:text-[#5fd08f]'
                : 'border border-[#066bff] bg-white text-[#066bff] dark:bg-transparent dark:border-[#3b82f6] dark:text-[#60a5fa]'
            }`}
          >
            {owned.isEquipped ? 'Đang gắn ✓ — tháo' : 'Đã sở hữu — Gắn'}
          </button>
        ) : soldOut ? (
          <button
            disabled
            className="w-full cursor-not-allowed rounded-full border border-slate-200 bg-slate-100 py-2 text-[16px] font-medium text-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-400"
          >
            🔒 Hết suất
          </button>
        ) : (
          <button onClick={() => handleBuy(item)} disabled={busyId === item.id} className={BTN}>
            {busyId === item.id
              ? 'Đang xử lý…'
              : owned && item.isConsumable
              ? `Mua thêm (đang có ${owned.quantity})`
              : 'Mua ngay'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-6 py-6 pb-16">
      {/* Popup "Xem thử" — hiệu ứng áp lên đúng avatar + tên của user */}
      {tryOn && (
        <DialogComponent
          visible
          title="Xem thử hiệu ứng"
          size="md"
          onClose={() => setTryOn(null)}
        >
          <TryOnPreview item={tryOn} rarity={rarityOf(tryOn)} avatar={myAvatar} name={myName} />
        </DialogComponent>
      )}
      {/* Heading 40px + gradient */}
      <h1 className="mx-auto max-w-[640px] text-center text-[32px] font-semibold leading-[1.3] tracking-tight text-[#19052f] dark:text-white md:text-[40px]">
        Theo dõi{' '}
        <span className="bg-gradient-to-r from-[#00a8e2] to-[#00669a] bg-clip-text text-transparent">
          số dư
        </span>{' '}
        và sử dụng{' '}
        <span className="bg-gradient-to-r from-[#00a8e2] to-[#00669a] bg-clip-text text-transparent">
          Ca
        </span>{' '}
        để mở quyền &amp; vật phẩm
      </h1>

      {/* 3 thẻ header (bento) trong khung trắng */}
      <div className="w-full rounded-[24px] bg-white p-6 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-transparent dark:p-0 dark:shadow-none">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Số dư */}
          <div className="relative flex h-[160px] flex-col justify-center gap-3 overflow-hidden rounded-[12px] border-[1.5px] border-white/40 bg-gradient-to-b from-[rgba(162,231,79,0.7)] to-[rgba(236,245,171,0.7)] px-7 py-6">
            <div className="text-[16px] font-medium text-black">Số dư hiện tại</div>
            <div className="text-[28px] font-semibold text-black">{fmtCa(summary?.balance ?? 0)}</div>
            <DialogPayment onConfirm={() => loadSummary()} onCancel={() => {}} />
            <div className="pointer-events-none absolute right-5 top-1/2 flex size-14 -translate-y-1/2 items-center justify-center rounded-[14px] bg-[#f15f60] text-[26px] shadow-lg">💰</div>
          </div>
          {/* Quick Stats */}
          <div className="relative flex h-[160px] flex-col justify-center gap-2 overflow-hidden rounded-[12px] border-[1.5px] border-white/40 bg-gradient-to-b from-[rgba(139,193,255,0.7)] to-[rgba(206,255,254,0.7)] px-7 py-6">
            <div className="text-[16px] font-medium text-black">Quick Stats</div>
            <div className="text-[24px] font-semibold text-black">
              {(summary?.totalEarnedCa ?? 0).toLocaleString('vi-VN')}{' '}
              <span className="text-[13px] font-normal">Ca đã kiếm</span>
            </div>
            <div className="text-[24px] font-semibold text-black">
              {(summary?.totalSpentCa ?? 0).toLocaleString('vi-VN')}{' '}
              <span className="text-[13px] font-normal">Ca đã tiêu</span>
            </div>
            <div className="pointer-events-none absolute right-5 top-1/2 flex size-14 -translate-y-1/2 items-center justify-center rounded-[14px] bg-[#8282ff] text-[26px] shadow-lg">🔄</div>
          </div>
          {/* Giao dịch gần nhất */}
          <div className="relative flex h-[160px] flex-col justify-center gap-2 overflow-hidden rounded-[12px] border-[1.5px] border-white/40 bg-gradient-to-b from-[rgba(244,197,175,0.7)] to-[rgba(255,235,206,0.7)] px-7 py-6">
            <div className="text-[16px] font-medium text-black">Giao dịch gần nhất</div>
            <div className="text-[28px] font-semibold text-black">
              {(summary?.lastAmountCa ?? 0) > 0 ? '+' : ''}
              {fmtCa(summary?.lastAmountCa ?? 0)}
            </div>
            {summary?.lastLabel && (
              <div className="inline-block w-fit rounded-full bg-white px-2.5 py-0.5 text-[12px] text-black">
                {summary.lastLabel}
              </div>
            )}
            <div className="pointer-events-none absolute right-5 top-1/2 flex size-14 -translate-y-1/2 items-center justify-center rounded-[14px] bg-[#f56144] text-[26px] shadow-lg">🏆</div>
          </div>
        </div>

        {/* 3 ví TÁCH BẠCH rõ nguồn Ca: Ca nạp (qua cổng thanh toán, không ghi "tiền thật" — NĐ 52/2024) · Ca thu nhập (rút được) · Ca đào (game, KHÔNG phải tiền) */}
        {buckets && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex items-center gap-3 rounded-[12px] border border-[#DEE0E6] bg-[#f6fbff] p-4 dark:border-slate-700 dark:bg-slate-800">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-[#e0f0ff] text-[20px]">💳</span>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-600 dark:text-slate-300">Ca nạp <span className="text-[11px] text-slate-400">(nạp qua cổng thanh toán)</span></div>
                <div className="text-[18px] font-semibold text-[#19052f] dark:text-white">{fmtCa(buckets.topUpBalance)}</div>
                <div className="text-[11px] text-slate-400">Chạy quảng cáo · Donate · Mua sắm</div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-[12px] border border-[#DEE0E6] bg-[#fff8f0] p-4 dark:border-slate-700 dark:bg-slate-800">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-[#ffe9d1] text-[20px]">🎁</span>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-600 dark:text-slate-300">Ca thu nhập <span className="text-[11px] text-slate-400">(rút được)</span></div>
                <div className="text-[18px] font-semibold text-[#19052f] dark:text-white">{fmtCa(buckets.incomeBalance)}</div>
                <div className="text-[11px] text-slate-400">Rút về ngân hàng · Mua sắm</div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-[12px] border border-[#DEE0E6] bg-[#f3fbf4] p-4 dark:border-slate-700 dark:bg-slate-800">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-[#d9f5dd] text-[20px]">⛏️</span>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-600 dark:text-slate-300">Ca đào <span className="text-[11px] text-slate-400">(game)</span></div>
                <div className="text-[18px] font-semibold text-[#19052f] dark:text-white">{fmtCa(buckets.minedBalance)}</div>
                <div className="text-[11px] text-slate-400">Chơi game kiếm · KHÔNG phải tiền · không rút</div>
              </div>
            </div>
          </div>
        )}

        {/* Biểu phí rút tiền + lối sang chỗ rút (tab Doanh thu trên trang cá nhân), trước đây Ví Ca không nói rút ở đâu, phí bao nhiêu. */}
        {buckets && (
          <div className="mt-3 flex flex-col gap-2">
            <WithdrawFeeScheduleBox collapsible />
            <Link
              to="/profile?tab=history-donate"
              className="self-start text-sm font-medium text-[#215C91] underline dark:text-blue-300"
            >
              Rút Ca thu nhập về ngân hàng
            </Link>
          </div>
        )}
      </div>

      {/* walletOnly (trang Ví Ca thuần): chỉ hiện dashboard số dư, ẩn Marketplace. */}
      {!walletOnly && (loading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Spin />
        </div>
      ) : (
        <div className="flex w-full items-start gap-6">
          {/* Mục lục bên trái (desktop) */}
          <aside className="sticky top-[88px] hidden w-[240px] shrink-0 self-start lg:block">
            <nav className="rounded-[20px] bg-white p-3 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-slate-800 dark:shadow-none">
              <div className="flex items-center gap-2 px-3 py-2 text-[13px] font-semibold uppercase tracking-wide text-slate-400">
                <ListTree size={16} /> Mục lục
              </div>
              <ul className="flex flex-col gap-1">
                {SECTIONS.map((s) => {
                  const active = activeSection === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => scrollToSection(s.id)}
                        className={[
                          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] font-medium transition-colors',
                          active
                            ? 'bg-[#0057FF] text-white shadow-[0_4px_12px_rgba(0,87,255,0.35)]'
                            : 'text-[#657384] hover:bg-[#EAEFFA] dark:text-slate-300 dark:hover:bg-slate-700',
                        ].join(' ')}
                      >
                        <s.Icon size={18} className="shrink-0" />
                        <span className="truncate">{s.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Nội dung */}
          <div className="flex min-w-0 flex-1 flex-col gap-10 rounded-[20px] bg-white p-6 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-transparent dark:p-0 dark:shadow-none">
            {/* Mục lục cuộn ngang (mobile) */}
            <div className="-mx-2 flex gap-2 overflow-x-auto px-2 pb-1 lg:hidden">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  className={[
                    'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium',
                    activeSection === s.id
                      ? 'bg-[#0057FF] text-white'
                      : 'bg-[#EAEFFA] text-[#657384] dark:bg-slate-700 dark:text-slate-300',
                  ].join(' ')}
                >
                  <s.Icon size={14} /> {s.title}
                </button>
              ))}
            </div>

          {/* Marketplace */}
          <Section id="store-marketing" title="Nạp Ca">
            {TOPUP_PACKAGES.map((baseVnd) => (
              <div key={baseVnd} className={CARD}>
                <div className="text-center text-[13px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Nạp Ca
                </div>
                <div className={PRICE_BOX}>
                  <span className={`text-[30px] font-semibold leading-[38px] ${PRICE}`}>
                    {fmtVnd(baseVnd)}
                  </span>
                </div>
                {/* Cho thấy RÕ phép đổi tiền → Ca ngay trên thẻ, thay vì bắt người dùng
                    tự nhẩm từ hai con số rời nhau. */}
                <div className="flex w-full flex-col items-center gap-1">
                  <ArrowDown size={18} className="text-[#066bff]" />
                  <span className="text-[20px] font-semibold leading-7 text-[#19052f] dark:text-white">
                    {fmtCa(pricing.caFor(baseVnd))}
                  </span>
                </div>
                {/* Bấm "Nạp ngay" là đi thẳng sang cổng thanh toán, nên số tiền THỰC TRẢ
                    phải nằm ngay đây — không để người dùng tới cổng mới biết. */}
                <div className="flex w-full flex-col items-center gap-0.5 rounded-[12px] bg-slate-50 px-3 py-2 dark:bg-white/5">
                  <span className="text-[13px] text-slate-500 dark:text-slate-400">Thanh toán</span>
                  <span className="text-[18px] font-semibold leading-6 text-[#19052f] dark:text-white">
                    {fmtVnd(pricing.chargeVnd(baseVnd))}
                  </span>
                </div>
                <p className="min-h-[40px] flex-1 text-center text-[13px] font-normal leading-5 text-slate-500 dark:text-slate-400">
                  {fmtVnd(pricing.vndPerCa)} = 1 Ca · Đã gồm phí cổng thanh toán ({pricing.feeLabel}).
                  Ca vào ví ngay sau khi thanh toán.
                </p>
                <button onClick={() => handleTopup(baseVnd)} className={BTN}>
                  Nạp ngay
                </button>
              </div>
            ))}
          </Section>

          {nametags.length > 0 && (
            <Section id="store-nametag" title="Nametag đặc biệt">
              {nametags.map((i) => renderItemCard(i))}
            </Section>
          )}

          {effects.length > 0 && (
            <Section id="store-effect" title="Hiệu ứng avatar">
              {effects.map((i) => renderItemCard(i))}
            </Section>
          )}

          {others.length > 0 && (
            <Section id="store-other" title="Khác">
              {others.map((i) => renderItemCard(i))}
            </Section>
          )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default WalletPage;
